/**
 * Forge tab: smelter and crafter slots. The recipe picker is a bottom sheet
 * where recipes are bought with GC, as in IPM, and then run on repeat.
 */

import { ALLOYS, ITEMS, RARITY_LABEL, resource } from '../../content/resources';
import { hasRecipe, slotCost, type Workshop } from '../../sim/actions';
import { craftSpeed, craftYield, sellPrice, smeltSpeed, smeltYield } from '../../sim/economy';
import { hasFeature } from '../../sim/projects';
import { canStart } from '../../sim/tick';
import { recipeInputs } from '../../sim/rooms';
import type { SlotState } from '../../sim/state';
import { fmtFine, fmtTime, price } from '../format';
import { icon } from '../icons';
import { btn, ingredients, resGlyph, resName, type View } from '../view';

function slotCard(v: View, workshop: Workshop, slot: SlotState, index: number): string {
  const { state, stats } = v;
  const speed = workshop === 'smelter' ? smeltSpeed(state, stats) : craftSpeed(state, stats);
  const open = btn(slot.recipe ? 'Change' : 'Choose', 'openRecipe', { cls: slot.recipe ? 'ghost' : 'green', data: { workshop, slot: index } });
  if (!slot.recipe) {
    return `<div class="vm-row" style="--accent:var(--line)" data-key="${workshop}-${index}">
      <div class="vm-row-main"><div class="vm-row-name vm-faint">${workshop === 'smelter' ? 'Smelter' : 'Crafter'} ${index + 1} · idle</div>
      <div class="vm-row-effect">Pick a recipe; it runs on repeat while inputs last.</div></div>${open}</div>`;
  }
  const def = resource(slot.recipe);
  const recipe = def.recipe!;
  const pct = slot.running ? Math.min(100, (100 * slot.progress) / recipe.time) : 0;
  const waiting = !slot.running && !canStart(state, def.id);
  const status = slot.running ? fmtTime((recipe.time - slot.progress) / speed) : waiting ? 'Waiting' : 'Starting';
  return `<div class="vm-row${waiting ? ' warn' : ''}" style="--accent:${def.color}" data-key="${workshop}-${index}">
    <div class="vm-row-main">
      <div class="vm-row-name">${resName(def.id)} <span class="vm-lv ${waiting ? 'gold' : 'cyan'}">${status}</span></div>
      <div class="vm-row-effect">${ingredients(state, recipeInputs(state, def.id))}</div>
      <div class="vm-bar"><i style="width:${pct.toFixed(1)}%"></i></div>
    </div>${open}</div>`;
}

function workshopSection(v: View, workshop: Workshop): string {
  const { state, stats } = v;
  const slots = workshop === 'smelter' ? state.run.smelters : state.run.crafters;
  const isSmelter = workshop === 'smelter';
  const speed = isSmelter ? smeltSpeed(state, stats) : craftSpeed(state, stats);
  const yieldPct = Math.round((isSmelter ? smeltYield(stats) : craftYield(stats)) * 100);
  const cost = slotCost(state, workshop);
  return `<section class="vm-section" data-key="${workshop}">
    <div class="vm-section-head ${isSmelter ? 'gold' : 'violet'}">${isSmelter ? 'Smelters' : 'Crafters'}
      <span class="aside">×${fmtFine(speed)} speed${yieldPct ? ` · ${yieldPct}% double` : ''}</span></div>
    <div class="vm-rows">${slots.map((slot, i) => slotCard(v, workshop, slot, i)).join('')}</div>
    ${cost === null ? '' : `<div style="margin-top:8px">${btn(`Build another ${isSmelter ? 'smelter' : 'crafter'} · ${price('gc', cost)}`, 'buySlot', { cls: 'ghost wide', disabled: state.run.gc < cost, data: { workshop } })}</div>`}
  </section>`;
}

function locked(title: string, glyph: string, text: string): string {
  return `<section class="vm-section vm-empty">
    <div class="vm-empty-glyph">${glyph}</div>
    <div class="vm-section-head gold" style="justify-content:center">${title}</div>
    <p class="vm-note">${text}</p>
    ${btn('Open Projects', 'openProjects', { cls: 'violet' })}
  </section>`;
}

export function renderProduction(v: View): string {
  const { state } = v;
  if (!hasFeature(state, 'smelter')) {
    return locked('No smelter yet', icon('factory'), 'Research <b>Asteroid Miner</b>, then <b>Smelter</b>, in Projects. Until then, sell ore from the Planets tab or the Market.');
  }
  const crafter = hasFeature(state, 'crafter')
    ? workshopSection(v, 'crafter')
    : `<section class="vm-section" data-key="crafter-locked"><div class="vm-section-head violet">Crafters <span class="aside">locked</span></div>
       <p class="vm-note">Research <b>Crafter</b> to build parts: ${['hull_plating', 'velocity_chip', 'phase_cap'].map(resGlyph).join(' ')} and more.</p>
       ${btn('Open Projects', 'openProjects', { cls: 'violet' })}</section>`;
  return workshopSection(v, 'smelter') + crafter;
}

export function renderRecipeSheet(v: View, workshop: Workshop, slot: number): string {
  const { state, stats } = v;
  const speed = workshop === 'smelter' ? smeltSpeed(state, stats) : craftSpeed(state, stats);
  const pool = workshop === 'smelter' ? ALLOYS : ITEMS;
  const current = (workshop === 'smelter' ? state.run.smelters : state.run.crafters)[slot]?.recipe;
  // IPM lists every recipe; bought ones run, the next unbought one is offered for GC, the rest stay hidden.
  const firstLocked = pool.find((def) => !hasRecipe(state, def.id));
  const shown = pool.filter((def) => hasRecipe(state, def.id) || def === firstLocked);
  const rows = shown.map((def) => {
    const tag = def.rarity ? `<span class="vm-lv" style="border-color:${def.color};color:${def.color}">${RARITY_LABEL[def.rarity]}</span>` : '';
    const owned = hasRecipe(state, def.id);
    const action = owned
      ? btn(def.id === current ? 'Running' : 'Run', 'setRecipe', { cls: canStart(state, def.id) ? 'green' : 'ghost', data: { workshop, slot, recipe: def.id } })
      : btn(`Unlock<br>${price('gc', def.recipe!.unlock)}`, 'buyRecipe', { cls: 'gold buy', disabled: state.run.gc < def.recipe!.unlock, data: { id: def.id } });
    return `<div class="vm-row${def.id === current ? ' selected' : ''}${owned ? '' : ' warn'}" style="--accent:${def.color}" data-key="pick-${def.id}">
      <div class="vm-row-main">
        <div class="vm-row-name">${resName(def.id)} ${tag}</div>
        <div class="vm-row-effect">${ingredients(state, recipeInputs(state, def.id))}<br>${fmtTime(def.recipe!.time / speed)} each · sells ${price('gc', sellPrice(state, stats, def.id))}</div>
      </div>
      ${action}
    </div>`;
  }).join('');
  const hidden = pool.length - shown.length;
  return `<div class="vm-sheet-head">
      <h3>${workshop === 'smelter' ? 'Smelter' : 'Crafter'} ${slot + 1}</h3>
      ${btn('✕', 'closeSheet', { cls: 'ghost' })}
    </div>
    <div class="vm-rows">${rows}</div>
    ${hidden > 0 ? `<p class="vm-note" style="margin-top:8px">${hidden} more recipe${hidden === 1 ? '' : 's'} after this one.</p>` : ''}
    ${current ? `<div style="margin-top:8px">${btn('Stop this slot', 'setRecipe', { cls: 'ghost wide', data: { workshop, slot, recipe: '' } })}</div>` : ''}`;
}
